import { useState } from 'react'
import { MenuItem, Stack, TextField } from '@mui/material'
import type {
  BenefitSection,
  PolicyBenefitInfo,
  PolicyBenefitInfoAction,
  PolicyConfigurationDetail,
} from './BenefitPolicyService'
import { RecordDialog } from './RecordDialog'

interface BenefitInfoDialogProps {
  // The row being edited; absent means this is an add into `section`.
  benefitInfo?: PolicyBenefitInfo
  section: BenefitSection
  sectionLabel: string
  documents: PolicyConfigurationDetail['documents']
  isActive: boolean
  saving: boolean
  refusal?: string
  onClose: () => void
  onConfirm: (action: PolicyBenefitInfoAction) => void
}

/** What the member taps through to. One control, because the endpoint refuses a row holding both. */
type Target = 'none' | 'url' | 'document'

const TARGETS = [
  { value: 'none', label: 'No link' },
  { value: 'url', label: 'External URL' },
  { value: 'document', label: 'Plan document' },
] as const satisfies readonly { value: Target; label: string }[]

const targetOf = (benefitInfo: PolicyBenefitInfo | undefined): Target => {
  if (benefitInfo?.document != null) return 'document'
  if (benefitInfo?.url) return 'url'
  return 'none'
}

/**
 * Author one row of benefit information.
 *
 * Mounted only while it is up, like the plan-document dialog: its fields are seeded from the row
 * that was clicked, and one kept mounted would open on the previous row's half-typed title.
 */
export const BenefitInfoDialog = ({
  benefitInfo,
  section,
  sectionLabel,
  documents,
  isActive,
  saving,
  refusal,
  onClose,
  onConfirm,
}: BenefitInfoDialogProps) => {
  const [title, setTitle] = useState(benefitInfo?.title ?? '')
  const [summary, setSummary] = useState(benefitInfo?.summary ?? '')
  const [linkLabel, setLinkLabel] = useState(benefitInfo?.link_label ?? '')
  const [url, setUrl] = useState(benefitInfo?.url ?? '')
  const [document, setDocument] = useState<number | ''>(benefitInfo?.document ?? '')
  const [displayOrder, setDisplayOrder] = useState<number | ''>(benefitInfo?.display_order ?? 0)
  // Held rather than read back off the values: a freshly chosen "Plan document" has no document
  // yet, and deriving the choice would snap the control back to "No link" before the picker
  // could be used.
  const [target, setTarget] = useState<Target>(() => targetOf(benefitInfo))

  // A document this row already opens stays on the list even once it has been hidden from members,
  // labelled as such. Hiding one is not refused the way removing it is, so the row outlives the
  // visibility — and dropping it from the options would blank the picker while the id still travels
  // on every save, leaving the operator a refusal with nothing on screen pointing at its cause.
  const linked = documents.find(candidate => candidate.id === benefitInfo?.document)
  const hiddenLinked = linked?.is_visible_to_member === false ? linked : undefined
  const selectable = documents.filter(
    candidate => candidate.is_visible_to_member || candidate.id === hiddenLinked?.id,
  )

  const confirm = () => {
    // Every field travels, `null` included: the endpoint refuses a write that leaves one out,
    // because an omission cannot be told apart from a deliberate clear.
    const content = {
      title: title.trim() || null,
      summary: summary.trim() || null,
      link_label: target === 'none' ? null : linkLabel.trim() || null,
      url: target === 'url' ? url.trim() || null : null,
      document: target === 'document' && document !== '' ? document : null,
      display_order: displayOrder === '' ? 0 : displayOrder,
    }
    onConfirm(
      benefitInfo
        ? { action: 'update', benefit_info: benefitInfo.id, ...content }
        : { action: 'add', benefit_type: section, ...content },
    )
  }

  return (
    <RecordDialog
      title={`${benefitInfo ? 'Edit' : 'Add'} ${sectionLabel.toLowerCase()} info`}
      confirmLabel={benefitInfo ? 'Save' : 'Add info'}
      busy={saving}
      incomplete={title.trim() === '' && summary.trim() === ''}
      notice={
        isActive
          ? `This policy is live. Members see this on the ${sectionLabel} screen as soon as you save.`
          : undefined
      }
      refusal={refusal}
      testId="benefit-info"
      onClose={onClose}
      onConfirm={confirm}
    >
      <Stack direction="row" spacing={2}>
        <TextField
          fullWidth
          label="Title"
          value={title}
          onChange={event => setTitle(event.target.value)}
          data-testid="benefit-info-title"
        />
        <TextField
          type="number"
          label="Order"
          value={displayOrder}
          onChange={event =>
            setDisplayOrder(event.target.value === '' ? '' : Number(event.target.value))
          }
          helperText="Lower sorts first."
          sx={{ width: 120, flex: 'none' }}
          data-testid="benefit-info-display-order"
        />
      </Stack>

      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Summary"
        value={summary}
        onChange={event => setSummary(event.target.value)}
        helperText="A title or a summary is required — at least one of the two."
        data-testid="benefit-info-summary"
      />

      <TextField
        select
        label="Opens"
        value={target}
        onChange={event => setTarget(event.target.value as Target)}
        sx={{ width: 200 }}
        data-testid="benefit-info-target"
      >
        {TARGETS.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {target === 'url' && (
        <TextField
          fullWidth
          label="URL"
          value={url}
          onChange={event => setUrl(event.target.value)}
          data-testid="benefit-info-url"
        />
      )}
      {target === 'document' && (
        <TextField
          select
          fullWidth
          label="Document"
          value={document}
          onChange={event => setDocument(Number(event.target.value))}
          error={document !== '' && document === hiddenLinked?.id}
          // Otherwise only member-visible documents: the endpoint refuses the rest, so offering one
          // would be offering a refusal.
          helperText={
            document !== '' && document === hiddenLinked?.id
              ? 'Hidden from members. Make it visible under Plan documents, or pick another — saving this row is refused until then.'
              : selectable.length > 0
                ? undefined
                : 'Add a member-visible document in Plan documents first.'
          }
          data-testid="benefit-info-document"
        >
          {selectable.map(candidate => (
            <MenuItem key={candidate.id} value={candidate.id}>
              {candidate.name}
              {candidate.is_visible_to_member ? '' : ' (hidden from members)'}
            </MenuItem>
          ))}
        </TextField>
      )}
      {target !== 'none' && (
        <TextField
          fullWidth
          label="Link label"
          value={linkLabel}
          onChange={event => setLinkLabel(event.target.value)}
          helperText="What the member taps. Without it no link is shown."
          data-testid="benefit-info-link-label"
        />
      )}
    </RecordDialog>
  )
}
